import React from 'react';
import { Key, ExternalLink, Loader2, CheckCircle2, XCircle, Shield } from 'lucide-react';
import toast from 'react-hot-toast';
import { APIKeys, AIProviderConfig } from '../../lib/ai/types';
import { callExternalAPI } from '../../lib/ai/api-router';

interface APIKeyFormProps {
  keys: APIKeys;
  setKeys: (keys: APIKeys) => void;
  showKeys: Record<string, boolean>;
  setShowKeys: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  testingKeys: Record<string, boolean>;
  setTestingKeys: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  testResults: Record<string, 'success' | 'error' | null>;
  setTestResults: React.Dispatch<React.SetStateAction<Record<string, 'success' | 'error' | null>>>;
  testErrors?: Record<string, string>;
  setTestErrors?: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  saveSettings: (params: { newKeys?: APIKeys }) => void;
  smartRotateEnabled: boolean;
  setSmartRotateEnabled: (val: boolean) => void;
  fallbackEnabled: boolean;
  setFallbackEnabled: (val: boolean) => void;
  fallbackOrder: string[];
  setFallbackOrder: (val: string[]) => void;
  providers: AIProviderConfig[];
}

export const APIKeyForm: React.FC<APIKeyFormProps> = ({
  keys,
  setKeys,
  showKeys,
  setShowKeys,
  testingKeys,
  setTestingKeys,
  testResults,
  setTestResults,
  testErrors = {},
  setTestErrors = () => {},
  saveSettings,
  smartRotateEnabled,
  setSmartRotateEnabled,
  fallbackEnabled,
  setFallbackEnabled,
  fallbackOrder,
  setFallbackOrder,
  providers
}) => {

  const handleTestKey = async (providerId: string, keyIdx: number, keyValue: string) => {
    if (!keyValue) {
      toast.error('Please enter an API key first.');
      return;
    }
    
    const testId = `${providerId}-${keyIdx}`;
    setTestingKeys(prev => ({ ...prev, [testId]: true }));
    setTestResults(prev => ({ ...prev, [testId]: null }));
    setTestErrors(prev => ({ ...prev, [testId]: '' }));

    const toastId = toast.loading(`Testing ${providerId} infrastructure...`);
    
    try {
      console.log(`[Test Connection] Provider: ${providerId}, Status: Testing...`);
      const testPrompt = "Respond with exactly 'OK'.";
      const timeoutMs = providerId === 'together' ? 60000 : 15000;
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timed out')), timeoutMs)
      );

      const result = await Promise.race([
        callExternalAPI(testPrompt, { 
          testKey: keyValue,
          provider: providerId,
          skipReflection: true,
          disableFallback: true
        }),
        timeoutPromise
      ]) as any;

      // callExternalAPI returns APIResponse { response: string, model: string, provider: string }
      const responseText = typeof result === 'string' ? result : result?.response || '';

      if (responseText && (responseText.toLowerCase().includes('ok') || responseText.length > 0)) {
        console.log(`[Test Connection] Provider: ${providerId}, Status: 200 (Success)`);
        toast.success(`Γ£à Connected to ${providerId}!`, { id: toastId });
        setTestResults(prev => ({ ...prev, [testId]: 'success' }));
      } else {
        throw new Error(`Unexpected response from ${providerId}.`);
      }
    } catch (err: any) {
      console.error(`[AI Test] ${providerId} failed:`, err);
      
      let errorMsg = '';
      if (err) {
        if (typeof err === 'string') {
          errorMsg = err;
        } else if (typeof err.message === 'string') {
          errorMsg = err.message;
        } else if (err.message && typeof err.message === 'object') {
          errorMsg = JSON.stringify(err.message);
        } else if (err.error && typeof err.error === 'string') {
          errorMsg = err.error;
        } else if (err.error?.message && typeof err.error.message === 'string') {
          errorMsg = err.error.message;
        } else {
          errorMsg = JSON.stringify(err);
        }
      }
      const lowMsg = errorMsg.toLowerCase();
      
      // Map raw errors to friendly messages
      if (lowMsg.includes('401') || lowMsg.includes('invalid x-api-key') || lowMsg.includes('invalid api key') || lowMsg.includes('unauthorized')) {
        const consoles: Record<string, string> = {
          gemini: 'https://aistudio.google.com/app/apikey',
          openai: 'https://platform.openai.com/api-keys',
          claude: 'https://console.anthropic.com/settings/keys',
          mistral: 'https://console.mistral.ai/api-keys/',
          together: 'https://api.together.xyz/settings/api-keys',
          groq: 'https://console.groq.com/keys',
          minimax: 'https://platform.minimaxi.com/user-center/basic-information/interface-key'
        };
        const url = consoles[providerId] || 'the provider console';
        
        // Specific checks for providers
        if (providerId === 'claude') {
          errorMsg = `Your Claude account has no credits. Please add funds at https://console.anthropic.com/settings/plans.`;
        } else if (providerId === 'together') {
          errorMsg = `Together AI rejected your key. Please verify your key at https://api.together.xyz/settings/api-keys`;
        } else if (providerId === 'minimax') {
          errorMsg = `MinMax rejected your key. Error: invalid api key. Please verify your key at the MinMax console.`;
        } else {
          errorMsg = `Invalid API key. Check your key at ${url}`;
        }
      } else if (lowMsg.includes('insufficient_quota') || lowMsg.includes('billing') || lowMsg.includes('balance') || lowMsg.includes('credit')) {
        const billingUrls: Record<string, string> = {
          claude: 'https://console.anthropic.com/settings/plans',
          openai: 'https://platform.openai.com/settings/billing',
          gemini: 'https://aistudio.google.com/app/billing'
        };
        const billingUrl = billingUrls[providerId] || 'the provider billing page';
        errorMsg = `Your account has no credits. Please add funds at ${billingUrl}`;
      } else if (lowMsg.includes('429') || lowMsg.includes('quota exceeded') || lowMsg.includes('resource_exhausted') || lowMsg.includes('rate limit')) {
        // Extract retry seconds if present (e.g. "Try again in 30s")
        const retryMatch = lowMsg.match(/(\d+)\s*s/);
        const retrySec = retryMatch ? retryMatch[1] : null;
        errorMsg = retrySec 
          ? `Free tier rate limit reached. Please wait ~${retrySec}s and try again.` 
          : "Free tier rate limit reached. Please wait a few seconds and try again.";
      } else if (lowMsg.includes('404') || lowMsg.includes('model not found')) {
        errorMsg = "Model not available. Please check the model name in AI Settings.";
      } else if (lowMsg.includes('unexpected response') || lowMsg.includes('json') || lowMsg.includes('syntax error')) {
        errorMsg = `Unable to connect to ${providerId}. The API endpoint may have changed. Please contact support.`;
      } else if (lowMsg.includes('fetch') || lowMsg.includes('network') || lowMsg.includes('failed to fetch')) {
        errorMsg = `Could not reach ${providerId}. Check your internet connection.`;
      } else {
        errorMsg = errorMsg || 'Check your key and network connection';
      }

      console.log(`[Test Connection] Provider: ${providerId}, Status: Failed (${errorMsg})`);
      toast.error(`Γ¥î ${providerId} failed: ${errorMsg}`, { id: toastId, duration: 6000 });
      setTestResults(prev => ({ ...prev, [testId]: 'error' }));
      setTestErrors(prev => ({ ...prev, [testId]: errorMsg }));
    } finally {
      setTestingKeys(prev => ({ ...prev, [testId]: false }));
    }
  };

  const fullFallbackOrder = React.useMemo(() => {
    const order = [...fallbackOrder];
    (providers || []).forEach(p => {
      if (!order.includes(p.id)) {
        order.push(p.id);
      }
    });
    return order;
  }, [fallbackOrder, providers]);

  const activeFallbackProviders = fullFallbackOrder.filter(pid => {
    if (pid === 'local') return true;
    const keyVal = keys[pid];
    if (!keyVal) return false;
    if (Array.isArray(keyVal)) return keyVal.some(k => k.trim() !== '');
    return typeof keyVal === 'string' && keyVal.trim() !== '';
  });

  return (
    <section className="p-6 rounded-2xl border border-[var(--t-border)] bg-[var(--t-surface)]">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2" style={{ color: 'var(--t-text)' }}>
            <Key className="w-5 h-5 text-yellow-500" />
            BYO API Keys
          </h3>
          <p className="text-sm" style={{ color: 'var(--t-text-muted)' }}>
            Enable premium cloud models by adding your own keys.
          </p>
        </div>
        <div className="flex items-center gap-6">
           <div className="flex items-center gap-3">
             <span className="text-xs font-medium" style={{ color: 'var(--t-text-muted)' }}>Smart Rotate</span>
             <div
               onClick={() => setSmartRotateEnabled(!smartRotateEnabled)}
               className={`toggle-switch ${smartRotateEnabled ? 'is-on' : 'is-off'}`}
             >
               <div className="toggle-knob" />
             </div>
           </div>
           <div className="flex items-center gap-3">
             <span className="text-xs font-medium" style={{ color: 'var(--t-text-muted)' }}>API Fallback</span>
             <div
               onClick={() => setFallbackEnabled(!fallbackEnabled)}
               className={`toggle-switch ${fallbackEnabled ? 'is-on' : 'is-off'}`}
             >
               <div className="toggle-knob" />
             </div>
           </div>
        </div>
      </div>
      
      {fallbackEnabled && (
        <div className="mb-8 p-4 rounded-xl bg-[var(--t-bg)] border border-[var(--t-border)]">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-xs font-black uppercase tracking-widest" style={{ color: 'var(--t-text-muted)' }}>
              Fallback Priority Order
            </h4>
            <span className="text-[10px]" style={{ color: 'var(--t-text-muted)' }}>
              Drag or use arrows to prioritize failover
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {activeFallbackProviders.map((pid, activeIdx) => {
              const provider = providers.find(p => p.id === pid) || { name: pid, id: pid };
              return (
                <div 
                  key={pid}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[var(--t-surface)] border border-[var(--t-border)] group/item shadow-sm"
                >
                  <span className="text-xs font-bold" style={{ color: 'var(--t-text)' }}>
                    {activeIdx + 1}. {provider.name}
                  </span>
                  <div className="flex items-center gap-1 opacity-0 group-hover/item:opacity-100 transition-opacity">
                    <button
                      onClick={() => {
                        if (activeIdx === 0) return;
                        const actualIdx = fullFallbackOrder.indexOf(pid);
                        const prevPid = activeFallbackProviders[activeIdx - 1];
                        const prevActualIdx = fullFallbackOrder.indexOf(prevPid);
                        
                        const newOrder = [...fullFallbackOrder];
                        [newOrder[prevActualIdx], newOrder[actualIdx]] = [newOrder[actualIdx], newOrder[prevActualIdx]];
                        setFallbackOrder(newOrder);
                      }}
                      className="p-1 hover:bg-[var(--t-bg)] rounded text-[var(--t-text-muted)]"
                      title="Move Up"
                    >
                      &larr;
                    </button>
                    <button
                      onClick={() => {
                        if (activeIdx === activeFallbackProviders.length - 1) return;
                        const actualIdx = fullFallbackOrder.indexOf(pid);
                        const nextPid = activeFallbackProviders[activeIdx + 1];
                        const nextActualIdx = fullFallbackOrder.indexOf(nextPid);

                        const newOrder = [...fullFallbackOrder];
                        [newOrder[nextActualIdx], newOrder[actualIdx]] = [newOrder[actualIdx], newOrder[nextActualIdx]];
                        setFallbackOrder(newOrder);
                      }}
                      className="p-1 hover:bg-[var(--t-bg)] rounded text-[var(--t-text-muted)]"
                      title="Move Down"
                    >
                      &rarr;
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="space-y-6">
        {(providers || []).filter(p => p.id !== 'local').map((p) => (
          <div key={p.id} className="group">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--t-text)' }}>
                {p.name} Key
              </label>
              <div className="flex items-center gap-4">
                <a 
                  href={
                    p.id === 'gemini' ? 'https://aistudio.google.com/app/apikey' : 
                    p.id === 'openai' ? 'https://platform.openai.com/api-keys' : 
                    p.id === 'claude' ? 'https://console.anthropic.com/settings/keys' :
                    p.id === 'minimax' ? 'https://platform.minimaxi.com/user-center/basic-information/interface-key' : 
                    p.id === 'groq' ? 'https://console.groq.com/keys' :
                    p.id === 'mistral' ? 'https://console.mistral.ai/api-keys/' :
                    p.id === 'cohere' ? 'https://dashboard.cohere.com/api-keys' :
                    p.id === 'deepseek' ? 'https://platform.deepseek.com/api_keys' :
                    p.id === 'together' ? 'https://api.together.xyz/settings/api-keys' : '#'
                  } 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[10px] flex items-center gap-1 hover:underline font-medium"
                  style={{ color: 'var(--t-primary)' }}
                >
                  Get Key <ExternalLink className="w-2.5 h-2.5" />
                </a>
                <button 
                  onClick={() => setShowKeys(prev => ({ ...prev, [p.id]: !prev[p.id] }))}
                  className="text-[10px] text-[var(--t-text-muted)] hover:text-[var(--t-text)]"
                >
                  {showKeys[p.id] ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>
            <div className="space-y-3">
              {(() => {
                const currentKeys = Array.isArray(keys[p.id]) ? (keys[p.id] as string[]) : [keys[p.id] as string || ''];
                return (
                  <>
                    {currentKeys.map((keyVal, idx) => (
                      <div key={idx} className="relative group/key">
                        <input
                          type={showKeys[p.id] ? 'text' : 'password'}
                          value={keyVal}
                          onChange={(e) => {
                            const updated = [...currentKeys];
                            updated[idx] = e.target.value;
                            const newKeys = { ...keys, [p.id]: updated };
                            setKeys(newKeys);
                          }}
                          onBlur={() => {
                            const updated = [...currentKeys];
                            updated[idx] = keyVal;
                            const newKeys = { ...keys, [p.id]: updated };
                            saveSettings({ newKeys });
                          }}
                          placeholder={`API Key ${idx + 1}...`}
                          className="w-full h-10 px-4 pr-24 rounded-lg border border-[var(--t-border)] bg-[var(--t-bg)] focus:ring-2 focus:ring-[var(--t-primary)] focus:border-transparent transition-all outline-none font-mono text-xs"
                          style={{ color: 'var(--t-text)' }}
                        />
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                          <button
                            onClick={() => handleTestKey(p.id, idx, keyVal)}
                            disabled={testingKeys[`${p.id}-${idx}`] || !keyVal}
                            title="Test Connection"
                            className="text-[10px] bg-[var(--t-surface)] border border-[var(--t-border)] hover:bg-[var(--t-border)] disabled:opacity-50 px-2 py-1 rounded transition-colors flex items-center gap-1 font-bold"
                            style={{ color: 'var(--t-text)' }}
                          >
                            {testingKeys[`${p.id}-${idx}`] ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : testResults[`${p.id}-${idx}`] === 'success' ? (
                              <CheckCircle2 className="w-3 h-3 text-green-500" />
                            ) : testResults[`${p.id}-${idx}`] === 'error' ? (
                              <XCircle className="w-3 h-3 text-red-500" />
                            ) : (
                              'Test'
                            )}
                          </button>
                          {testResults[`${p.id}-${idx}`] === 'error' && testErrors[`${p.id}-${idx}`] && (
                            <div className="absolute top-full left-0 mt-1 z-20 bg-red-500 text-white text-[9px] px-2 py-1 rounded shadow-lg whitespace-nowrap">
                              {testErrors[`${p.id}-${idx}`]}
                            </div>
                          )}
                          {currentKeys.length > 1 && (
                            <button 
                              onClick={() => {
                                const updated = currentKeys.filter((_, i) => i !== idx);
                                const newKeys = { ...keys, [p.id]: updated };
                                setKeys(newKeys);
                                saveSettings({ newKeys });
                              }}
                              className="text-xs text-red-500 p-1 bg-[var(--t-surface)] border rounded hover:bg-red-500/10 opacity-0 group-hover/key:opacity-100 transition-opacity"
                            >
                              &times;
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                    <button
                      onClick={() => {
                        const updated = [...currentKeys, ''];
                        const newKeys = { ...keys, [p.id]: updated };
                        setKeys(newKeys);
                      }}
                      className="text-[10px] font-bold uppercase tracking-wider text-[var(--t-text-muted)] hover:text-[var(--t-primary)] transition-colors flex items-center gap-1"
                    >
                      + Add Failover Key
                    </button>
                  </>
                );
              })()}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 p-4 rounded-xl bg-[var(--t-bg)] border border-[var(--t-border)] flex items-start gap-4">
        <div className="p-2 rounded-lg bg-indigo-500/10">
          <Shield className="w-5 h-5 text-indigo-500" />
        </div>
        <div>
          <h4 className="text-sm font-bold mb-1" style={{ color: 'var(--t-text)' }}>Security & Privacy</h4>
          <p className="text-xs leading-relaxed" style={{ color: 'var(--t-text-muted)' }}>
            Your API keys are stored in your private encrypted profile on Supabase. 
            External AI providers do not receive your personal data unless you explicitly trigger them 
            via a fallback or by setting them as your primary intelligence layer.
          </p>
        </div>
      </div>
    </section>
  );
};

